import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSImageField } from '@/ui/components/input/FSImageField';
import { UploadImageServiceS3 } from '@/ui/components/input/FSImageField.utils/upload-image-service-s3';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { seasonDurationIsMaximumOneYear } from './form-validations';

export function ProjectVenueForm({
  project,
  buttons,
  onSubmit,
}: {
  project: ProjectVenue;
  buttons?: React.ReactNode;
  onSubmit: (data: ProjectVenue) => void;
}) {
  const organisationId = useAppSelector(selectOrganisationInfos).id.toString();
  const imageDestinationFolder = organisationId;

  const { handleSubmit, register, formState, control, getValues, watch } =
    useForm<ProjectVenue>({
      defaultValues: project,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ProjectVenue) => {
    onSubmit({
      ...data,
      maxCapacity: parseInt(data.maxCapacity as unknown as string),
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack>
        <Controller
          name="image"
          control={control}
          render={({ field }) => (
            <FSImageField
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              uploadImageService={
                new UploadImageServiceS3(imageDestinationFolder)
              }
            />
          )}
        />
        <FSTextField
          label={translate('ProjectsVenue.Form.season-name')}
          defaultValue={project.name}
          register={{
            ...register('name', {
              required: translate('ProjectsVenue.Form.name-is-required'),
            }),
          }}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <FSDateField
            sx={{ width: '100%' }}
            value={project.startDate || ''}
            label={translate('Form.start-date')}
            control={control}
            required={translate('Common.field-is-required')}
            name="startDate"
            error={!!errors.startDate}
            helperText={errors.startDate?.message}
          />
          <FSDateField
            sx={{ width: '100%' }}
            value={project.endDate || ''}
            label={translate('Form.end-date')}
            minDate={dayjs(watch('startDate'))}
            control={control}
            required={translate('Common.field-is-required')}
            name="endDate"
            error={!!errors.endDate}
            helperText={errors.endDate?.message}
            validate={(date) =>
              seasonDurationIsMaximumOneYear(date, getValues('startDate') || '')
            }
          />
        </Stack>
        <FSTextField
          defaultValue={project.location}
          label={translate('ProjectsVenue.Form.location')}
          register={{ ...register('location') }}
          error={!!errors.location}
          helperText={errors.location?.message}
        />
        <FSTextField
          type="number"
          label={translate('ProjectsVenue.Form.max-capacity')}
          defaultValue={project.maxCapacity}
          maxLength={5}
          register={{ ...register('maxCapacity') }}
          error={!!errors.maxCapacity}
          helperText={errors.maxCapacity?.message}
        />
        {buttons && buttons}
      </Stack>
    </form>
  );
}
